import { Modal, Form, Button, message, Alert, Input } from "antd";
import { map } from "lodash";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import AppInput from "../../../common/AppInput";

const AddPaymentVoucher = ({ visible, handleCloseModal, handleAddRecord, initialRequisitionMemoId, initialRequisitionMemo }) => {
  const {
    addPaymentVouchersLoading,
    addPaymentVouchersSuccess,
    addPaymentVouchersError,
    addPaymentVouchersMessage,
    paymentVouchersMetaDataLoading,
    paymentVouchersMetaData,
    paymentVouchers,
  } = useSelector((state) => state.paymentVouchersState);
  
  const [form] = Form.useForm();
  
  const closeModal = () => {
    handleCloseModal(false);
    form.resetFields();
  };
  
  const onFinish = (values) => {
    values.created_at = new Date();
    handleAddRecord(values, paymentVouchers);
  };

  // Find the matching requisition memo from metadata
  const matchingMemo = useMemo(() => {
    if (
      initialRequisitionMemoId &&
      paymentVouchersMetaData?.requisitionMemos &&
      Array.isArray(paymentVouchersMetaData.requisitionMemos)
    ) {
      // Try matching by ID first
      let found = paymentVouchersMetaData.requisitionMemos.find((memo) => {
        return memo.id === initialRequisitionMemoId || 
          String(memo.id) === String(initialRequisitionMemoId) ||
          Number(memo.id) === Number(initialRequisitionMemoId);
      });
      
      // If not found by ID, try matching by memo_number
      if (!found && initialRequisitionMemo?.memo_number) {
        found = paymentVouchersMetaData.requisitionMemos.find((memo) => {
          return memo.memo_number === initialRequisitionMemo.memo_number ||
            String(memo.memo_number) === String(initialRequisitionMemo.memo_number) ||
            Number(memo.memo_number) === Number(initialRequisitionMemo.memo_number);
        });
      }
      
      if (found) {
        return found;
      } else if (initialRequisitionMemo && initialRequisitionMemo.id === initialRequisitionMemoId) {
        // If we have the initial requisition memo details, use that even if not in metadata
        return initialRequisitionMemo;
      }
    } else if (initialRequisitionMemo && initialRequisitionMemo.id === initialRequisitionMemoId) {
      // If metadata not loaded yet but we have initial memo, use it
      return initialRequisitionMemo;
    }
    
    return null;
  }, [initialRequisitionMemoId, initialRequisitionMemo, paymentVouchersMetaData?.requisitionMemos]);

  // Helper function to get project name from memo (handles both metadata and details formats)
  const getProjectName = (memo) => {
    return memo?.projectDetails?.name || memo?.project_name || 'Unknown Project';
  };

  // Compute options - only show the matching requisition if initialRequisitionMemoId is provided
  const requisitionOptions = useMemo(() => {
    if (initialRequisitionMemoId && matchingMemo) {
      // Only show the one matching requisition
      const projectName = getProjectName(matchingMemo);
      const activities = map(matchingMemo?.activities || [], "activity").join(", ");
      return [
        {
          value: matchingMemo.id,
          label: activities ? `(${activities}) - ${projectName}` : projectName,
        },
      ];
    }
    
    // Show all requisitions if no initialRequisitionMemoId
    return map(paymentVouchersMetaData?.requisitionMemos || [], (memo) => {
      const projectName = getProjectName(memo);
      const activities = map(memo?.activities || [], "activity").join(", ");
      return {
        value: memo.id,
        label: activities ? `(${activities}) - ${projectName}` : projectName,
      };
    });
  }, [initialRequisitionMemoId, matchingMemo, paymentVouchersMetaData?.requisitionMemos]);

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  // Set the value after metadata is loaded and modal is visible
  useEffect(() => {
    if (visible && initialRequisitionMemoId && matchingMemo) {
      const valueToSet = matchingMemo.id;
      
      // Check current value to avoid unnecessary updates
      const currentValue = form.getFieldValue('requisition_memo');
      if (currentValue === valueToSet || String(currentValue) === String(valueToSet)) {
        return;
      }
      
      // Set value with multiple attempts to ensure it sticks
      const setValue = (shouldValidate = false) => {
        form.setFieldsValue({ 
          requisition_memo: valueToSet 
        }, { shouldValidate });
      };
      
      // If metadata is loaded and options are available, verify value exists in options
      if (!paymentVouchersMetaDataLoading && requisitionOptions.length > 0) {
        const valueExistsInOptions = requisitionOptions.some(
          (option) => 
            option.value === valueToSet || 
            String(option.value) === String(valueToSet) || 
            Number(option.value) === Number(valueToSet)
        );
        
        if (!valueExistsInOptions) {
          return;
        }
      }
      
      // Set immediately without validation
      setValue(false);
      
      // Set again after a short delay
      const timer1 = setTimeout(() => setValue(false), 150);
      
      // Set again after longer delay and validate to clear errors
      const timer2 = setTimeout(() => {
        setValue(true);
        // Also explicitly clear validation errors
        setTimeout(() => {
          form.setFields([{
            name: 'requisition_memo',
            errors: [],
          }]);
        }, 100);
      }, 600);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [
    visible,
    initialRequisitionMemoId,
    matchingMemo,
    paymentVouchersMetaDataLoading,
    requisitionOptions,
    form,
  ]);

  useEffect(() => {
    if (addPaymentVouchersSuccess && addPaymentVouchersMessage) {
      message.success(addPaymentVouchersMessage);
      form.resetFields();
    }
  }, [addPaymentVouchersMessage, addPaymentVouchersSuccess, form]);
  return (
    <div>
      <Modal
        onCancel={closeModal}
        open={visible}
        footer={null}
        title="CREATE FUNDS ADVANCE/WITHDRAW FORM"
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={onFinish}
        >
          {!addPaymentVouchersSuccess && addPaymentVouchersError && (
            <Alert type="error" showIcon message={addPaymentVouchersError} />
          )}
          <AppInput
            rules={[
              { required: true, message: "Please select a requisition form" },
            ]}
            label="Requisition Form"
            name="requisition_memo"
            type="select"
            options={requisitionOptions}
            itemAttributes={{
              validateTrigger: 'onBlur', // Only validate on blur, not on change
            }}
            inputAttributes={{
              loading: paymentVouchersMetaDataLoading,
            }}
          />
          <AppInput
            rules={[
              {
                required: true,
                message: "Please select a cash or bank account",
              },
            ]}
            label="Cash/Bank Account"
            name="account_id"
            type="select"
            options={map(paymentVouchersMetaData?.accounts, (account) => ({
              value: account.id,
              label: account.name,
            }))}
            inputAttributes={{
              loading: paymentVouchersMetaDataLoading,
            }}
          />
          <AppInput
            name="category"
            type="select"
            label="Category"
            options={[
              { label: "Expense", value: "Expense" },
              { label: "Asset", value: "Asset" },
            ]}
            rules={[
              {
                required: true,
              },
            ]}
          />
          <AppInput
            name="mode_of_payment"
            type="select"
            label="Mode of Payment"
            options={[
              { label: "Cash", value: "Cash" },
              { label: "Bank", value: "Bank" },
              { label: "Cheque", value: "Cheque" },
              { label: "Mobile Money", value: "Mobile Money" },
            ]}
            rules={[
              {
                required: true,
              },
            ]}
          />
          <Form.Item
            rules={[{ required: true, message: "Please add a cheque number" }]}
            name="cheque_number"
            label="Cheque Number"
          >
            <Input />
          </Form.Item>
          <Form.Item>
            <Button
              loading={addPaymentVouchersLoading}
              htmlType="submit"
              type="primary"
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddPaymentVoucher;
